import { downloadFile } from './client';

/**
 * The six modules that have a CSV export. The union is the type AND the URL
 * segment, verified against `routes/api.php`: `case-requests` (not
 * `requests`) and `knowledge-base` (not `knowledge`) are the route prefixes
 * Tasks 3 and 4 registered.
 */
export type ExportModule =
  | 'cases'
  | 'case-requests'
  | 'escalations'
  | 'justifications'
  | 'knowledge-base'
  | 'replacements';

/**
 * Hand a downloaded blob to the browser under `filename`.
 *
 * The object URL is revoked in a `finally`, so an exception from the click
 * cannot leak it — a leaked object URL pins the whole blob in memory for the
 * life of the document. The revoke is deferred one tick so it cannot race the
 * browser's own asynchronous start of the download.
 */
function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);

  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    /*
     * DEFERRED BY ONE TICK, not called inline.
     *
     * `anchor.click()` returns as soon as the event has been dispatched;
     * in some Firefox and WebKit builds the download itself is started
     * asynchronously AFTER that, so revoking in the same synchronous run can
     * tear the blob down before the browser has read it and the download is
     * silently dropped. Chromium starts it during dispatch, which is why the
     * inline form looks correct locally and fails in the field.
     *
     * The guarantee is unchanged: this is still a `finally`, so the URL is
     * revoked even when `click()` throws, and the throw still propagates —
     * `finally` swallows nothing here.
     */
    setTimeout(() => {
      URL.revokeObjectURL(url);
    }, 0);
  }
}

/**
 * Download a module's export with the filters currently on screen and hand it
 * to the browser.
 */
export async function saveExport(
  module: ExportModule,
  params: Record<string, string | number | boolean | undefined> = {},
): Promise<void> {
  const { blob, filename } = await downloadFile(`/${module}/export`, params);
  saveBlob(blob, filename);
}

/**
 * `GET /cases/{uuid}/pdf` (M-15c) — the staff case PDF. A sibling of
 * `saveExport()` rather than a mode of it: that function's path is
 * `/{module}/export` across six modules, and this is one fixed per-case
 * route, so folding them together would mean a parameter that means
 * "which of two unrelated URL shapes".
 */
export async function saveCasePdf(uuid: string): Promise<void> {
  const { blob, filename } = await downloadFile(
    `/cases/${uuid}/pdf`,
    {},
    'application/pdf, application/json',
  );
  saveBlob(blob, filename);
}
